import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { IcUserComment } from "@racoon/icons";
import { Namespace, useDateFnsLocale, useT, type TranslationKey } from "@racoon/i18n";
import { EMPTY_CELL, fromTimestamp, getEnumName, isUndefinedEnum } from "@racoon/lib";
import {
  GroupChevron,
  Hyperlink,
  StatusPill,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type CellContext,
  type Column,
} from "@racoon/ui";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { plannedContainerCount } from "~/shared/lib/job-containers";
import { formatVolume, getJobVolume } from "~/shared/lib/job-metrics";
import { OrderAuthor } from "../components/order-author";
import { JobStatusPill } from "../components/job-status-pill";
import { formatMapDay, formatMapTime, formatMapWindow } from "./job-map-format";
import { isCompleted } from "./job-map-rows";
import { TwoLines } from "../components/job-map/two-lines";
import { OrganizationLabel } from "../components/job-map/organization-label";
import type { OrderRow } from "./adapters";

type Props = {
  /** Opens the job in the edit dialog, like the registry's id link. */
  onOrderIdClick?: (row: OrderRow) => void;
  onCustomerClick?: (customer: ConnectProto.Organization) => void;
};

/**
 * The columns of the order map (Карта заявок), one row per order: it reads the order's
 * current job (see `jobMapRows`).
 * Widths are each column's minimum — headers, dates and the status and review pills
 * whole — so the map fits a 1440px screen; wider screens stretch them all alike.
 * The order's jobs open below it in a table of their own (`OrderJobsTable`).
 */
export const useJobMapColumns = ({ onOrderIdClick, onCustomerClick }: Props) => {
  const t = useT();
  const locale = useDateFnsLocale();
  // Another organization's zone job (or one not yet known as ours) opens no customer card
  const isOwnForWrite = useIsOwnOrganizationForWrite("job");

  return useMemo<Column<OrderRow>[]>(() => {
    const day = (date: Date) => formatMapDay(date, locale);
    const time = (date: Date) => formatMapTime(date, locale);
    const header = (key: TranslationKey<Namespace.Orders>) =>
      t(Namespace.Orders, key).toUpperCase();

    return [
      {
        id: "orderNumber",
        header: header("jobMap.columns.order"),
        accessor: (r) => r.rawJob.orderNumber || r.number,
        width: 122,
        cell: (info: CellContext<OrderRow, string>) => {
          const r = info.row.original;
          return (
            <div className="flex items-center gap-3">
              {/* Clicking the row opens the order's jobs. */}
              <GroupChevron isExpanded={!!r._isExpanded} />
              <div className="flex min-w-0 flex-col">
                <span className="text-xs leading-4 text-text-body">
                  {t(Namespace.Orders, "jobMap.columns.order")}
                </span>
                <span
                  className="truncate text-sm font-bold leading-5 text-text-body"
                  title={info.getValue()}
                >
                  {onOrderIdClick ? (
                    <Hyperlink onOpen={() => onOrderIdClick(r)} data-testid="orders-job-link">
                      {info.getValue()}
                    </Hyperlink>
                  ) : (
                    info.getValue()
                  )}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "orderCustomer",
        header: header("jobMap.columns.customer"),
        accessor: (r) => r.customer,
        width: 197,
        cell: (info: CellContext<OrderRow, string>) => {
          const job = info.row.original.rawJob;
          const customer = job.serviceReceiver;
          if (!customer) return info.getValue();
          const canOpen =
            onCustomerClick && isOwnForWrite(job.serviceProviderId, job.serviceReceiverId) === true;
          return (
            <OrganizationLabel
              organization={customer}
              onOpen={canOpen ? () => onCustomerClick(customer) : undefined}
            />
          );
        },
      },
      {
        id: "orderStatus",
        header: header("jobMap.columns.status"),
        accessor: (r) => r.status,
        width: 229,
        cell: (info: CellContext<OrderRow, string>) => {
          const r = info.row.original;
          const review = r.rawJob.dispatcherReviewStatus;
          const reviewName = getEnumName(ConnectProto.DispatcherReviewStatusSchema, review);
          return (
            <div className="flex items-center gap-2">
              <JobStatusPill status={r.status} />
              {/* A serviced order shows whether the dispatcher has checked it. */}
              {isCompleted(r.rawJob) &&
                (review === ConnectProto.DispatcherReviewStatus.APPROVED ? (
                  <StatusPill tone="bg-green-100 text-status-success">
                    {t(Namespace.Orders, "jobMap.reviewApproved")}
                  </StatusPill>
                ) : (
                  reviewName &&
                  !isUndefinedEnum(reviewName) && (
                    <StatusPill tone="bg-slate-100 text-text-body">
                      {t(Namespace.Enum, reviewName)}
                    </StatusPill>
                  )
                ))}
            </div>
          );
        },
      },
      {
        id: "containerGroup",
        header: header("jobMap.columns.containerGroup"),
        accessor: (r) => {
          const group = r.rawJob.containerGroup;
          const name = (group?.containerType ?? r.rawJob.containerType)?.name;
          if (!name) return EMPTY_CELL;
          return group ? `${name} (${group.numberOfContainers})` : name;
        },
        width: 106,
      },
      {
        id: "containers",
        header: header("jobMap.columns.quantity"),
        accessor: (r) => {
          const count = plannedContainerCount(r.rawJob);
          return count ? t(Namespace.Orders, "jobMap.containers", { count }) : EMPTY_CELL;
        },
        width: 66,
      },
      {
        id: "plannedVolume",
        header: header("jobMap.columns.volume"),
        accessor: (r) => {
          const volume = getJobVolume(r.rawJob);
          if (!volume) return EMPTY_CELL;
          return t(Namespace.Orders, "jobMap.volume", { volume: formatVolume(volume) });
        },
        width: 62,
      },
      {
        id: "jobType",
        header: header("jobMap.columns.jobType"),
        accessor: (r) => r.typeText,
        width: 143,
      },
      {
        id: "orderComment",
        header: header("jobMap.columns.comment"),
        accessor: (r) => r.comment,
        width: 110,
        align: "center" as const,
        // Always drawn: grey without a comment; blue with one, which hovering shows.
        cell: (info: CellContext<OrderRow, string>) => {
          const comment = info.getValue();
          if (!comment) {
            return (
              <div className="flex w-full justify-center">
                <IcUserComment className="size-8 text-slate-400" aria-hidden />
              </div>
            );
          }
          // Clicks stop here: the row around it opens and closes the order.
          const stop = (e: React.MouseEvent) => e.stopPropagation();
          return (
            <div className="flex w-full justify-center">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={t(Namespace.Orders, "jobMap.columns.comment")}
                      className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      onClick={stop}
                    >
                      <IcUserComment className="size-8 text-icon-action" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap break-words">
                    {comment}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
      },
      {
        id: "submittedAt",
        header: header("jobMap.columns.submittedAt"),
        // The map lists an order's current attempt only: a re-issued order reads by its
        // re-issue's created_at (it keeps the order's author).
        accessor: (r) => r.createdAt,
        width: 128,
        cell: (info: CellContext<OrderRow, string>) => {
          const createdAt = info.getValue() ? new Date(info.getValue()) : undefined;
          return (
            <div className="flex items-center gap-2">
              <OrderAuthor row={info.row.original} size="sm" />
              {createdAt && <TwoLines first={day(createdAt)} second={time(createdAt)} />}
            </div>
          );
        },
      },
      {
        id: "plannedFor",
        header: header("jobMap.columns.plannedFor"),
        accessor: (r) => r.rawJob.twStartAt,
        width: 147,
        cell: (info: CellContext<OrderRow, unknown>) => {
          const start = fromTimestamp(info.row.original.rawJob.twStartAt);
          const end = fromTimestamp(info.row.original.rawJob.twEndAt);
          if (!start) return EMPTY_CELL;
          return <TwoLines first={day(start)} second={formatMapWindow(start, end, locale)} />;
        },
      },
    ];
  }, [t, locale, isOwnForWrite, onOrderIdClick, onCustomerClick]);
};
